"use client";

import { useState } from "react";
import {
  Checkbox,
  Notice,
  type Option,
  PillGroup,
  SelectField,
  TextAreaField,
} from "@/components/tool-kit/primitives";
import {
  alphaOptions,
  ExampleButton,
} from "@/components/tools/stats-tests/kit";
import { WilcoxonResults } from "@/components/tools/stats-tests/wilcoxon-results";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import { parseNumberList } from "@/lib/parse-number";
import type { Alternative } from "@/lib/tool-kit/stats-tests/format";
import { wilcoxonSignedRank } from "@/lib/tool-kit/stats-tests/wilcoxon";

/** English strings. A translated content file overrides these through `ui`. */
export const WILCOXON_SIGNED_RANK_TEST_CALCULATOR_STRINGS = {
  inputLabel: "Input",
  pairedInput: "Two paired lists",
  differencesInput: "A list of differences",
  firstLabel: "First measurement (e.g. before)",
  secondLabel: "Second measurement (e.g. after), in the same order",
  differencesLabel:
    "Differences — numbers separated by commas, spaces, or new lines",
  dataPlaceholder: "42, 38, 51, 45",
  valuesDetected: "one: value|other: values",
  alternativeLabel: "Alternative hypothesis",
  twoSided: "Two-sided: differences are not centred on zero",
  less: "Differences tend to be negative",
  greater: "Differences tend to be positive",
  alternativeHint: "Differences are first − second.",
  alphaLabel: "Significance level (α)",
  continuity: "Continuity correction (normal approximation)",
  tryExample: "Try an example",
  exampleFirst: "42, 38, 51, 45, 36, 48, 40, 53, 39, 47",
  exampleSecond: "37, 35, 44, 46, 30, 39, 40, 45, 35, 37",
  exampleDifferences: "5, 3, 7, -1, 6, 9, 0, 8, 4, 10",
  results: "Results",
  tStatistic: "T (also called W): the smaller of T⁺ and T⁻",
  tPlus: "T⁺ (sum of positive ranks)",
  tMinus: "T⁻ (sum of negative ranks)",
  pExact: "p-value (exact)",
  pNormal: "p-value (normal approximation)",
  zScore: "z (from T⁺)",
  effectR: "Effect size r = |z| / √n",
  rankBiserial: "Matched-pairs rank-biserial correlation",
  pairsUsed: "Pairs used",
  methodExact:
    "Exact p-value: no tied differences and 25 or fewer non-zero pairs, so p comes from the exact distribution of T⁺. The normal approximation gives {p} for comparison.",
  methodTies:
    "Normal approximation with a tie correction: some absolute differences are tied, so the exact distribution does not apply.",
  methodTiesCc:
    "Normal approximation with tie and continuity corrections: some absolute differences are tied, so the exact distribution does not apply.",
  methodSize:
    "Normal approximation: there are more than 25 non-zero pairs, where the approximation is accurate.",
  methodSizeCc:
    "Normal approximation with a continuity correction: there are more than 25 non-zero pairs, where the approximation is accurate.",
  zeros:
    "one: {count} zero difference was dropped before ranking.|other: {count} zero differences were dropped before ranking.",
  zerosNote:
    "That is Wilcoxon's method. Pratt's method ranks the zeros too and then discards their ranks, which can change the result slightly.",
  decisionReject:
    "Reject H₀ at α = {alpha}: p = {p} is below α — the differences are not centred on zero.",
  decisionKeep:
    "Do not reject H₀ at α = {alpha}: p = {p} is not below α — no evidence of a systematic difference.",
  ranksHeading: "Signed ranks",
  ranksHint:
    "Absolute differences are ranked from smallest to largest; tied values share their average rank.",
  pair: "Pair",
  difference: "d",
  absolute: "|d|",
  signedRank: "Signed rank",
  dropped: "dropped",
  ranksHidden: "The rank table is shown for up to 40 pairs.",
  howCalculated: "How this was calculated",
  workingContinuity:
    "With the continuity correction (0.5 toward the mean), z = {z}.",
  apaHeading: "APA-style result",
  apaTemplate: "*T* = {t}, *z* = {z}, *p* {p}, *r* = {r}",
  apaNoteExact:
    'p is exact. Report the median difference (or the medians of both measurements) alongside the test; R calls T⁺ "V".',
  apaNoteNormal:
    "p is from the normal approximation. Report the median difference (or the medians of both measurements) alongside the test.",
  copyApa: "Copy APA result",
  copied: "Copied!",
  promptPaired:
    "Enter the two measurements in matching order — the same number of values in each box.",
  promptDifferences: "Enter at least two differences.",
  errorLength:
    "The lists must pair up one to one, but they are {first} and {second} long.",
  errorZeros:
    "Every difference is zero, so there is nothing to rank. The two measurements are identical.",
} as const;

type Strings = typeof WILCOXON_SIGNED_RANK_TEST_CALCULATOR_STRINGS;
type InputMode = "paired" | "differences";

function differencesFrom(
  mode: InputMode,
  first: number[],
  second: number[],
  differences: number[]
): number[] | null {
  if (mode === "differences") {
    return differences.length >= 2 ? differences : null;
  }
  if (first.length < 2 || first.length !== second.length) {
    return null;
  }
  return first.map((value, i) => value - second[i]);
}

function promptText(
  mode: InputMode,
  first: number[],
  second: number[],
  strings: Strings,
  fmt: (value: number) => string
): string {
  if (mode === "differences") {
    return strings.promptDifferences;
  }
  if (
    first.length >= 2 &&
    second.length >= 2 &&
    first.length !== second.length
  ) {
    return t(strings.errorLength, {
      first: fmt(first.length),
      second: fmt(second.length),
    });
  }
  return strings.promptPaired;
}

export default function WilcoxonSignedRankTestCalculatorWidget() {
  const strings = useToolUi(WILCOXON_SIGNED_RANK_TEST_CALCULATOR_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [mode, setMode] = useState<InputMode>("paired");
  const [firstText, setFirstText] = useState("");
  const [secondText, setSecondText] = useState("");
  const [diffText, setDiffText] = useState("");
  const [alternative, setAlternative] = useState<Alternative>("two-sided");
  const [alpha, setAlpha] = useState("0.05");
  const [continuity, setContinuity] = useState(true);

  const first = parseNumberList(firstText);
  const second = parseNumberList(secondText);
  const typed = parseNumberList(diffText);
  const differences = differencesFrom(mode, first, second, typed);
  const result = differences
    ? wilcoxonSignedRank(differences, { continuity, alternative })
    : null;
  const count = (n: number) =>
    `${fmt(n)}${tPlural(strings.valuesDetected, locale, n)}`;
  const alternatives: Option<Alternative>[] = [
    { value: "two-sided", label: strings.twoSided },
    { value: "less", label: strings.less },
    { value: "greater", label: strings.greater },
  ];

  function fillExample() {
    setFirstText(strings.exampleFirst);
    setSecondText(strings.exampleSecond);
    setDiffText(strings.exampleDifferences);
  }

  return (
    <div className="space-y-6">
      <PillGroup
        label={strings.inputLabel}
        name="wsr-input"
        onChange={setMode}
        options={[
          { value: "paired", label: strings.pairedInput },
          { value: "differences", label: strings.differencesInput },
        ]}
        value={mode}
      />
      {mode === "paired" ? (
        <div className="grid gap-4 sm:grid-cols-2">
          <TextAreaField
            hint={count(first.length)}
            id="wsr-first"
            label={strings.firstLabel}
            onChange={setFirstText}
            placeholder={strings.dataPlaceholder}
            value={firstText}
          />
          <TextAreaField
            hint={count(second.length)}
            id="wsr-second"
            label={strings.secondLabel}
            onChange={setSecondText}
            placeholder={strings.dataPlaceholder}
            value={secondText}
          />
        </div>
      ) : (
        <TextAreaField
          hint={count(typed.length)}
          id="wsr-differences"
          label={strings.differencesLabel}
          onChange={setDiffText}
          placeholder={strings.dataPlaceholder}
          value={diffText}
        />
      )}
      <div className="grid gap-4 sm:grid-cols-2">
        <SelectField
          hint={strings.alternativeHint}
          id="wsr-alternative"
          label={strings.alternativeLabel}
          onChange={setAlternative}
          options={alternatives}
          value={alternative}
        />
        <SelectField
          id="wsr-alpha"
          label={strings.alphaLabel}
          onChange={setAlpha}
          options={alphaOptions(fmt)}
          value={alpha}
        />
      </div>
      <Checkbox
        checked={continuity}
        label={strings.continuity}
        onChange={setContinuity}
      />
      <ExampleButton label={strings.tryExample} onClick={fillExample} />
      {result ? (
        <WilcoxonResults
          alpha={Number(alpha)}
          continuity={continuity}
          fmt={fmt}
          locale={locale}
          r={result}
          strings={strings}
        />
      ) : (
        <Notice>
          {differences
            ? strings.errorZeros
            : promptText(mode, first, second, strings, fmt)}
        </Notice>
      )}
    </div>
  );
}
